import addDays from 'date-fns/addDays';
import format from 'date-fns/format';
import getISOWeek from 'date-fns/getISOWeek';
import parseISO from 'date-fns/parseISO';
import {
	TUtilisationAnyTruckPeriod,
	TUtilisationCategory,
	TUtilisationColourBasis,
	TUtilisationLtlTruckPeriod,
	TUtilisationMetricKey,
	TUtilisationMode,
	TUtilisationPeriod,
	TUtilisationRow,
	TUtilisationShipment,
	TUtilisationTruckPeriod,
} from '@types';
import { csvCell } from './csvUtils';

export const ALL_LANES = 'All lanes';

/** The metric selector's options, in the order it lists them. Pallets comes first as the default (CON-1382). */
export const METRIC_LABELS: Record<TUtilisationMetricKey, string> = {
	pallet_quantity: 'Utilisation % (Pallets)',
	weight: 'Utilisation % (Weight)',
	pallet_spaces: 'Utilisation % (Pallet Spaces)',
};

export const UTILISATION_CATEGORY_LABELS: Record<TUtilisationCategory, string> = {
	woolworths: 'Woolworths',
	coles: 'Coles',
	aldi: 'Aldi',
	sto: 'Stock Transfer Orders',
	others: 'Others',
};

/** Week buckets come back as the Monday of the week, so label them by ISO week. */
export const weekLabel = (week: string): string => `Week ${getISOWeek(parseISO(week))}`;

/**
 * The dates a week column covers.
 *
 * An ISO week number is not something anyone reads a date off, so the column
 * carries its Monday to Sunday alongside. The year sits on the end only, since
 * a week never straddles two of them in this report's buckets.
 */
export const weekRangeLabel = (week: string): string => {
	const start = parseISO(week);
	const end = addDays(start, 6);

	return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
}

/**
 * The figure a cell shows.
 *
 * LTL is cost per kg and ignores the metric selector entirely; the API does not
 * return capacity for it and no truck is involved. FTL applies the selected
 * metric to every row uniformly, which is what the POC does, rather than
 * following each row's temperature-driven headline_metric.
 */
export const cellValue = (
	period: TUtilisationPeriod | null,
	mode: TUtilisationMode,
	metric: TUtilisationMetricKey,
): number | null => {
	if (!period) return null;
	if (mode === 'ltl') return period.cost_per_kg ?? null;
	return period.metrics?.[metric]?.percent ?? null;
}

/** Moved and capacity for one cell, used to total the All lanes row. */
const cellTotals = (
	period: TUtilisationPeriod | null,
	metric: TUtilisationMetricKey,
): { moved: number; capacity: number } | null => {
	if (!period?.metrics) return null;
	const source = period.metrics[metric];
	if (!source) return null;
	return { moved: source.moved, capacity: source.capacity };
}

/**
 * The pinned total row. A real sum of moved over capacity across every lane in
 * view, never an average of the lane percentages, which would weight a one
 * shipment lane the same as a fifty shipment one.
 */
export const allLanesRow = (
	rows: TUtilisationRow[],
	weeks: string[],
	mode: TUtilisationMode,
	metric: TUtilisationMetricKey,
): Record<string, number | null> => {
	const totals: Record<string, number | null> = {};

	weeks.forEach((week) => {
		let moved = 0;
		let capacity = 0;
		let spend = 0;
		let weight = 0;
		let seen = false;

		rows.forEach((row) => {
			const period = row.periods[week];
			if (!period) return;

			if (mode === 'ltl') {
				if (!period.weight_kg) return;
				spend += period.spend;
				weight += period.weight_kg;
				seen = true;
				return;
			}

			const cell = cellTotals(period, metric);
			if (!cell || cell.capacity <= 0) return;
			moved += cell.moved;
			capacity += cell.capacity;
			seen = true;
		});

		if (!seen) {
			totals[week] = null;
			return;
		}

		totals[week] = mode === 'ltl'
			? (weight > 0 ? spend / weight : null)
			: (capacity > 0 ? (moved / capacity) * 100 : null);
	});

	return totals;
}

/**
 * The previous populated week, so a value following a gap renders bare rather
 * than comparing against a week that is not on screen.
 */
export const previousValue = (values: (number | null)[], index: number): number | null => {
	for (let i = index - 1; i >= 0; i--) {
		if (values[i] !== null) return values[i];
	}
	return null;
}

export type TDeltaDirection = 'up' | 'down' | null;

export const deltaDirection = (current: number | null, previous: number | null): TDeltaDirection => {
	if (current === null || previous === null) return null;
	if (current > previous) return 'up';
	if (current < previous) return 'down';
	return null;
}

/** The utilisation an FTL figure is green at or above when colouring against the target. */
export const UTILISATION_TARGET_PERCENT = 95;

export const COLOUR_BASIS_LABELS: Record<TUtilisationColourBasis, string> = {
	target: `Above or below ${UTILISATION_TARGET_PERCENT}%`,
	trend: 'Compared to the week before',
};

export type TCellTone = 'favourable' | 'unfavourable' | null;

/**
 * Whether a figure reads green, red or neither.
 *
 * Only FTL is coloured. How LTL should be judged is not yet known, so it is
 * left plain rather than assumed (CON-1368).
 */
export const cellTone = (
	value: number | null,
	previous: number | null,
	mode: TUtilisationMode,
	basis: TUtilisationColourBasis,
): TCellTone => {
	if (mode === 'ltl' || value === null) return null;

	if (basis === 'target') {
		return value >= UTILISATION_TARGET_PERCENT ? 'favourable' : 'unfavourable';
	}

	const direction = deltaDirection(value, previous);
	if (!direction) return null;
	return direction === 'up' ? 'favourable' : 'unfavourable';
}

/** Percentages are never clamped: over capacity is real and AP need to see it. */
export const formatCell = (value: number | null, mode: TUtilisationMode): string => {
	if (value === null) return '–';
	return mode === 'ltl' ? `$${value.toFixed(2)}` : `${value.toFixed(2)}%`;
}

/**
 * The summary grid never reads above 100%. The lane and shipment levels keep the
 * real figure, since that is where a load-planning error or bad source data
 * shows up (CON-1309).
 */
export const capAtFull = (value: number | null, mode: TUtilisationMode): number | null =>
	mode === 'ftl' && value !== null ? Math.min(value, 100) : value;

/**
 * The grid as CSV.
 *
 * Built from the same rows, totals and formatting the table renders, so the
 * export matches what is on screen for the same filters (AC1) rather than
 * re-deriving the figures and risking a different answer.
 */
export const utilisationCsv = (
	rows: TUtilisationRow[],
	weeks: string[],
	mode: TUtilisationMode,
	metric: TUtilisationMetricKey,
): string => {
	const header = ['Shipment Lane', 'Temperature', ...weeks.map(weekLabel)];
	const totals = allLanesRow(rows, weeks, mode, metric);

	const lines = [
		header.map(csvCell).join(','),
		[ALL_LANES, '', ...weeks.map((week) => formatCell(capAtFull(totals[week] ?? null, mode), mode))]
			.map(csvCell).join(','),
		...rows.map((row) => [
			row.lane,
			row.temperature,
			...weeks.map((week) => formatCell(capAtFull(cellValue(row.periods[week] ?? null, mode, metric), mode), mode)),
		].map(csvCell).join(',')),
	];

	return lines.join('\n');
}

const wholeNumber = (value: number | null | undefined): string =>
	value === null || value === undefined ? '–' : Math.round(value).toLocaleString('en-AU');

const money = (value: number): string =>
	`$${value.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * The lane breakdown's columns, in the order it draws them.
 *
 * Shared with the panel rather than restated here, so an export and the table it
 * was taken from cannot end up naming or ordering their columns differently.
 * The first two are the week and the truck, which every line carries; the rest
 * are what `breakdownValues` returns, in step.
 */
export const BREAKDOWN_LABELS: Record<TUtilisationMode, string[]> = {
	ftl: [
		'Week',
		'Booked Truck Size',
		'Utilisation % (Weight)',
		'Utilisation % (Pallet Spaces)',
		'# Shipments',
		'Pallet Spaces',
		'Truck Capacity',
		'Weight (kg)',
		'Truck Capacity (kg)',
		'Pallet Quantity',
		'Truck Size Max',
		'Utilisation % (Pallets)',
	],
	ltl: [
		'Week',
		'Booked Truck Size',
		'Price / kg',
		'# Shipments',
		'Weight (kg)',
		'Price',
		'Pallet Spaces',
		'Pallet Quantity',
	],
};

/** FTL is the mode with a denominator, so it is the one carrying metrics. */
export const isFtlTruck = (period: TUtilisationAnyTruckPeriod): period is TUtilisationTruckPeriod =>
	'metrics' in period;

/**
 * One breakdown line as text, the cells after the week and the truck.
 *
 * A week the lane did not run still gets a line, so a missing period fills the
 * row with dashes rather than dropping it and letting the weeks read crooked.
 */
export const breakdownValues = (
	period: TUtilisationAnyTruckPeriod | null,
	mode: TUtilisationMode,
): string[] => {
	const width = BREAKDOWN_LABELS[mode].length - 2;

	if (!period) return Array.from({ length: width }, () => '–');

	if (isFtlTruck(period)) {
		const { pallet_spaces, pallet_quantity, weight } = period.metrics;

		return [
			formatCell(weight.percent, 'ftl'),
			formatCell(pallet_spaces.percent, 'ftl'),
			wholeNumber(period.shipment_count),
			wholeNumber(pallet_spaces.moved),
			wholeNumber(pallet_spaces.capacity),
			wholeNumber(weight.moved),
			wholeNumber(weight.capacity),
			wholeNumber(pallet_quantity?.moved),
			wholeNumber(pallet_quantity?.capacity),
			formatCell(pallet_quantity?.percent ?? null, 'ftl'),
		];
	}

	const ltl = period as TUtilisationLtlTruckPeriod;

	return [
		formatCell(ltl.cost_per_kg, 'ltl'),
		wholeNumber(ltl.shipment_count),
		wholeNumber(ltl.weight_kg),
		money(ltl.spend),
		wholeNumber(ltl.pallet_spaces),
		wholeNumber(ltl.pallet_quantity),
	];
}

/** The week and truck lines a lane ran in one week, or a single blank line if none. */
const breakdownLines = (
	row: TUtilisationRow,
	weeks: string[],
	mode: TUtilisationMode,
): string[][] => weeks.flatMap((week) => {
	const trucks = row.periods[week]?.trucks;

	if (!trucks || Object.keys(trucks).length === 0) {
		return [[weekLabel(week), weekRangeLabel(week), '–', ...breakdownValues(null, mode)]];
	}

	return Object.entries(trucks).map(([truck, period]) => [
		weekLabel(week),
		weekRangeLabel(week),
		truck,
		...breakdownValues(period, mode),
	]);
});

/** The week column carries its dates in an export too, where there is no hover. */
const breakdownHeader = (mode: TUtilisationMode, ...before: string[]): string[] => {
	const [week, truck, ...rest] = BREAKDOWN_LABELS[mode];
	return [...before, week, 'Week Dates', truck, ...rest];
}

/**
 * Every lane in view broken down by week and truck size.
 *
 * The same figures the lane panels show, only for all of them at once: a single
 * percentage hides whether it is one truck running light or two of different
 * sizes averaging out, and that is the question this file answers.
 */
export const laneTruckCsv = (
	rows: TUtilisationRow[],
	weeks: string[],
	mode: TUtilisationMode,
): string => {
	const lines = [
		breakdownHeader(mode, 'Shipment Lane', 'Temperature').map(csvCell).join(','),
		...rows.flatMap((row) => breakdownLines(row, weeks, mode)
			.map((line) => [row.lane, row.temperature, ...line].map(csvCell).join(','))),
	];

	return lines.join('\n');
}

export type TShipmentColumnKey =
	| 'reference'
	| 'superdock'
	| 'dropAt'
	| 'lane'
	| 'truck'
	| 'pickupCompany'
	| 'pickupSuburb'
	| 'pickupState'
	| 'dropoffCompany'
	| 'dropoffSuburb'
	| 'dropoffState'
	| 'freightMode'
	| 'temperature'
	| 'weightPercent'
	| 'spacesPercent'
	| 'palletsPercent'
	| 'palletSpaces'
	| 'palletQuantity'
	| 'weight'
	| 'price'
	| 'pricePerKg';

export type TShipmentColumn = { key: TShipmentColumnKey; label: string };

/**
 * The shipment detail columns, in the order the drill-down panel draws them and
 * the week export writes them.
 *
 * FTL shipments carry both utilisation percentages and no price, since FTL is
 * truck utilisation only (CON-1374); LTL ones carry the price and price per kg,
 * matching the lane breakdown. The superdock column is only asked for where a
 * shipment rolled up into one, which is the one lane AP consolidate on.
 */
export const shipmentColumns = (mode: TUtilisationMode, showSuperdock: boolean): TShipmentColumn[] => [
	{ key: 'reference', label: 'Shipment Reference' },
	...(showSuperdock && mode === 'ftl' ? [{ key: 'superdock' as const, label: 'Superdock ID' }] : []),
	{ key: 'dropAt', label: 'Requested Drop-off Date' },
	{ key: 'lane', label: 'Shipment Lane Description' },
	{ key: 'truck', label: 'Booked Truck Size' },
	{ key: 'pickupCompany', label: 'Pick-up Company' },
	{ key: 'pickupSuburb', label: 'Pick-up Neighborhood' },
	{ key: 'pickupState', label: 'Pick-up State' },
	{ key: 'dropoffCompany', label: 'Drop-off Company' },
	{ key: 'dropoffSuburb', label: 'Drop-off Neighborhood' },
	{ key: 'dropoffState', label: 'Drop-off State' },
	{ key: 'freightMode', label: 'Freight Mode' },
	{ key: 'temperature', label: 'Temperature' },
	...(mode === 'ftl'
		? [
			{ key: 'weightPercent' as const, label: 'Utilisation % (Weight)' },
			{ key: 'spacesPercent' as const, label: 'Utilisation % (Pallet Spaces)' },
			{ key: 'palletsPercent' as const, label: 'Utilisation % (Pallets)' },
		]
		: []),
	{ key: 'palletSpaces', label: 'Pallet Spaces' },
	{ key: 'palletQuantity', label: 'Pallet Quantity' },
	{ key: 'weight', label: 'Weight (kg)' },
	...(mode === 'ltl'
		? [
			{ key: 'price' as const, label: 'Price' },
			{ key: 'pricePerKg' as const, label: 'Price / kg' },
		]
		: []),
];

/**
 * On the roll-up lane in every week, even one where nothing rolled up (Giorgia,
 * CON-1309). The backend owns that lane rule and flags each row; a payload from
 * before it did falls back to whether anything consolidated.
 */
export const showsSuperdock = (mode: TUtilisationMode, shipments: TUtilisationShipment[]): boolean =>
	mode === 'ftl' && shipments.some((shipment) => shipment.on_rollup_lane ?? shipment.is_consolidated);

export const shipmentText = (shipment: TUtilisationShipment, key: TShipmentColumnKey): string => {
	switch (key) {
	case 'reference': return shipment.reference || '–';
	case 'superdock': return (shipment.superdock_id ?? null) === null ? '–' : String(shipment.superdock_id);
	case 'dropAt': return shipment.drop_at;
	case 'lane': return shipment.lane;
	case 'truck': return shipment.truck ?? '–';
	case 'pickupCompany': return shipment.pickup_company ?? '–';
	case 'pickupSuburb': return shipment.pickup_suburb ?? '–';
	case 'pickupState': return shipment.pickup_state ?? '–';
	case 'dropoffCompany': return shipment.dropoff_company ?? '–';
	case 'dropoffSuburb': return shipment.dropoff_suburb ?? '–';
	case 'dropoffState': return shipment.dropoff_state ?? '–';
	case 'freightMode': return shipment.freight_mode ?? '–';
	case 'temperature': return shipment.temperature;
	case 'weightPercent': return formatCell(shipment.metrics?.weight.percent ?? null, 'ftl');
	case 'spacesPercent': return formatCell(shipment.metrics?.pallet_spaces.percent ?? null, 'ftl');
	case 'palletsPercent': return formatCell(shipment.metrics?.pallet_quantity?.percent ?? null, 'ftl');
	case 'palletSpaces': return wholeNumber(shipment.pallet_spaces);
	case 'palletQuantity': return wholeNumber(shipment.pallet_quantity);
	case 'weight': return wholeNumber(shipment.weight_kg);
	case 'price': return money(shipment.spend);
	case 'pricePerKg': return formatCell(shipment.cost_per_kg ?? null, 'ltl');
	default: return '–';
	}
}

/**
 * One lane's week: its truck breakdown, then the shipments behind it.
 *
 * Two tables in one file, separated by a blank line, because the question asked
 * of a single week is always both at once - what the trucks did, and which
 * shipments made them do it. A spreadsheet opens it as two blocks.
 */
export const weekShipmentsCsv = (
	row: TUtilisationRow,
	week: string,
	mode: TUtilisationMode,
	shipments: TUtilisationShipment[],
): string => {
	const breakdown = [
		breakdownHeader(mode, 'Shipment Lane', 'Temperature').map(csvCell).join(','),
		...breakdownLines(row, [week], mode)
			.map((line) => [row.lane, row.temperature, ...line].map(csvCell).join(',')),
	];

	const columns = shipmentColumns(mode, showsSuperdock(mode, shipments));
	const detail = [
		columns.map((column) => csvCell(column.label)).join(','),
		...shipments.map((shipment) => columns.map((column) => csvCell(shipmentText(shipment, column.key))).join(',')),
	];

	return [...breakdown, '', ...detail].join('\n');
}
